<template>
  <article
    class="promotion-ranking-banner relative overflow-hidden rounded-xl"
    :class="{ 'bg-white': !bannerUrl }"
    :style="cardStyle"
  >
    <div
      v-if="bannerUrl"
      class="promotion-ranking-banner__bg absolute inset-0 bg-cover bg-right bg-no-repeat"
      :style="bannerBackgroundStyle"
      aria-hidden="true"
    />
    <div
      v-if="bannerUrl"
      class="promotion-ranking-banner__overlay absolute left-0 top-0 h-full w-full"
      aria-hidden="true"
    />

    <div
      class="promotion-ranking-banner__inner relative z-10 grid gap-4 p-4 md:grid-cols-[auto_minmax(0,1fr)_20rem] md:items-center md:p-5"
    >
      <PromotionHexIcon
        class="shrink-0 max-md:mx-auto"
        :src="iconUrl"
        :alt="promotion.name"
        :color="accentColor"
        :size="64"
      />

      <div class="min-w-0 text-center md:text-left">
        <h2 class="text-lg font-bold" :class="bannerTitleClass">
          {{ promotion.name }}
        </h2>
        <div
          class="mt-1 flex items-center justify-center gap-1.5 text-sm md:justify-start"
          :class="bannerDateRowClass"
        >
          <OurSvgIcon
            class="shrink-0"
            :src="icons.Event"
            size="xs"
            :color="bannerUrl ? 'white' : 'gray'"
            variant="light"
          />
          <span>{{ dateLabel }}</span>
        </div>
      </div>

      <div
        class="promotion-ranking-banner__meta flex w-full flex-col gap-2 md:w-[20rem] md:shrink-0"
      >
        <div
          v-if="rewardText"
          class="promotion-ranking-banner__reward-row flex min-h-[3rem] w-full items-center gap-3 rounded-xl px-4 py-2.5 shadow-sm"
          :style="rewardRowStyle"
        >
          <OurSvgIcon
            class="shrink-0"
            :src="icons.Gift"
            size="lg"
            color="white"
            variant="light"
            aria-hidden="true"
          />
          <span class="text-sm font-bold leading-snug text-white">{{ rewardText }}</span>
        </div>

        <div
          v-if="statusTag"
          class="promotion-ranking-banner__status-row flex min-h-[3rem] w-full items-center gap-3 rounded-xl px-4 py-2.5 shadow-sm"
          :class="statusRowClass"
        >
          <OurSvgIcon
            class="shrink-0"
            :src="icons.Star"
            size="lg"
            :color="statusIconColor"
            variant="light"
            aria-hidden="true"
          />
          <span class="text-sm font-semibold">{{ statusTag.text }}</span>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import { mapGetters } from "vuex";
import DateService from "@/services/date.service";
import PromotionHexIcon from "@/views/dealer-promotions/_components/PromotionHexIcon.vue";

export default {
  name: "PromotionRankingBanner",

  components: {
    PromotionHexIcon,
  },

  props: {
    promotion: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters("promotions", ["getStatusByValue"]),

    icons: () => ({
      Gift: require("@/components/_bit/IconSvg/icons/Gift.svg"),
      Star: require("@/components/_bit/IconSvg/icons/Star.svg"),
      Event: require("@material-symbols/svg-400/rounded/event-fill.svg"),
    }),

    accentColor() {
      return this.promotion.color || "#3b82f6";
    },

    cardStyle() {
      const color = this.accentColor;

      return {
        "--promotion-color": color,
        "--promotion-color-badge": `${color}1a`,
        "--promotion-color-light": `${color}40`,
      };
    },

    bannerBackgroundStyle() {
      if (!this.bannerUrl) {
        return {};
      }

      return {
        backgroundImage: `url("${this.bannerUrl}")`,
      };
    },

    rewardRowStyle() {
      return {
        backgroundColor: this.accentColor,
      };
    },

    statusTag() {
      return this.getStatusByValue(this.promotion.status);
    },

    statusRowClass() {
      const colorMap = {
        green: "promotion-ranking-banner__status-row--green",
        blue: "promotion-ranking-banner__status-row--blue",
        gray: "promotion-ranking-banner__status-row--gray",
        orange: "promotion-ranking-banner__status-row--orange",
      };

      return colorMap[this.statusTag?.color] || colorMap.gray;
    },

    statusIconColor() {
      const colorMap = {
        green: "green",
        blue: "blue",
        gray: "gray",
        orange: "orange",
      };

      return colorMap[this.statusTag?.color] || "gray";
    },

    rewardText() {
      return this.promotion.reward || "";
    },

    bannerUrl() {
      return this.promotion.banner?.link || "";
    },

    iconUrl() {
      return this.promotion.icon?.link || "";
    },

    dateLabel() {
      const startsAt = this.promotion.startsAt;
      const endsAt = this.promotion.endsAt;

      if (startsAt && endsAt) {
        return `${DateService.dateConverter(startsAt)} – ${DateService.dateConverter(endsAt)}`;
      }

      return "–";
    },

    bannerTitleClass() {
      if (this.bannerUrl) {
        return "text-white promotion-ranking-banner__text-on-image";
      }

      return "text-[#1e204d]";
    },

    bannerDateRowClass() {
      if (this.bannerUrl) {
        return "text-white/90 promotion-ranking-banner__text-on-image promotion-ranking-banner__text-on-image--subtle";
      }

      return "text-gray-500";
    },
  },

  methods: {
    hexToRgba(hex, alpha) {
      const normalized = (hex || "#3b82f6").replace("#", "");

      if (normalized.length !== 6) {
        return `rgba(59, 130, 246, ${alpha})`;
      }

      const r = Number.parseInt(normalized.slice(0, 2), 16);
      const g = Number.parseInt(normalized.slice(2, 4), 16);
      const b = Number.parseInt(normalized.slice(4, 6), 16);

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
  },
};
</script>

<style lang="postcss" scoped>
.promotion-ranking-banner {
  @apply min-h-[7.5rem];
}

.promotion-ranking-banner__overlay {
  background: linear-gradient(
    90deg,
    rgba(17, 24, 39, 0.55) 0%,
    rgba(17, 24, 39, 0.35) 45%,
    rgba(17, 24, 39, 0.15) 100%
  );
}

.promotion-ranking-banner__text-on-image {
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.25);
}

.promotion-ranking-banner__text-on-image--subtle {
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

.promotion-ranking-banner__status-row--green {
  @apply bg-green-100 text-green-800;
}

.promotion-ranking-banner__status-row--blue {
  @apply bg-blue-100 text-blue-800;
}

.promotion-ranking-banner__status-row--gray {
  @apply bg-gray-100 text-gray-800;
}

.promotion-ranking-banner__status-row--orange {
  @apply bg-orange-100 text-orange-800;
}
</style>
